/**
 * Cata-Kor Quiz — Alpine.js component (HTML edition).
 * Local-storage persistence, step navigation, single/multiple selection, email validation.
 * Klaviyo / Shopify / Facebook hooks are intentionally omitted; will be wired in the .liquid port.
 */

(function () {
  "use strict";

  var STORAGE_ANSWERS = "catakor_quiz_answers";
  var STORAGE_STEP = "catakor_quiz_step";
  var STORAGE_QUIZ_ID = "catakor_quiz_id";
  var STORAGE_EMAIL = "catakor_quiz_email";
  var STEP_CSS_ID = "catakor-step-css";

  function generateQuizId() {
    return "quiz_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
  }

  function getQuizId() {
    var quizId = localStorage.getItem(STORAGE_QUIZ_ID);
    if (!quizId) {
      quizId = generateQuizId();
      localStorage.setItem(STORAGE_QUIZ_ID, quizId);
    }
    return quizId;
  }

  function getUrlParams() {
    var params = {};
    var queryString = window.location.search.substring(1);
    if (!queryString) return params;
    var pairs = queryString.split("&");
    for (var i = 0; i < pairs.length; i++) {
      var pair = pairs[i].split("=");
      if (pair[0]) {
        params[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || "");
      }
    }
    return params;
  }

  function normalizeToSteps(config) {
    if (config.steps && Array.isArray(config.steps) && config.steps.length > 0) {
      return config.steps;
    }
    var questions = config.questions || [];
    return questions.map(function (q) {
      return Object.assign({ stepType: "question" }, q);
    });
  }

  function injectStepCss(css) {
    var el = document.getElementById(STEP_CSS_ID);
    if (!el) {
      el = document.createElement("style");
      el.id = STEP_CSS_ID;
      document.head.appendChild(el);
    }
    el.textContent = css || "";
  }

  function escapeHtml(str) {
    if (str == null) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }


  function resolveAssetPath(value) {
    if (typeof value !== "string") return value;
    var map = window.CATA_KOR_ASSETS || {};
    if (map[value]) return map[value];
    return value.replace(/assets\/[A-Za-z0-9_.\/+@% -]+\.(?:png|jpe?g|webp|svg|woff2)/gi, function (assetPath) {
      return map[assetPath] || assetPath;
    });
  }

  function resolveAssetsInValue(value) {
    if (typeof value === "string") return resolveAssetPath(value);
    if (Array.isArray(value)) {
      return value.map(function (item) { return resolveAssetsInValue(item); });
    }
    if (value && typeof value === "object") {
      var out = {};
      Object.keys(value).forEach(function (key) {
        out[key] = resolveAssetsInValue(value[key]);
      });
      return out;
    }
    return value;
  }

  function isRealShopifyVariantId(variantId) {
    return /^\d+$/.test(String(variantId || ""));
  }

  function isRealShopifySellingPlanId(sellingPlanId) {
    return /^\d+$/.test(String(sellingPlanId || ""));
  }

  function findStepById(steps, id) {
    if (!steps) return null;
    for (var i = 0; i < steps.length; i++) {
      if (steps[i].id === id) return steps[i];
    }
    return null;
  }

  function answerLabelForValue(step, value) {
    if (!step || !step.answers || value == null) return "";
    for (var i = 0; i < step.answers.length; i++) {
      var a = step.answers[i];
      var v = a.value !== undefined ? a.value : a.id;
      if (v === value) return (a.text || "").trim();
    }
    return "";
  }

  /* Short labels for chips (avoid raw value slugs) */
  var Q7_CHIP = {
    energy_crash: "Afternoon crash",
    brain_fog: "Brain fog",
    couch: "Post-work fatigue",
    tired: "Waking unrefreshed",
    skin_dull: "Skin changes",
    recovery: "Slow recovery",
    sick: "Immune dips",
    self: "Not feeling like myself",
  };

  var Q8_CHIP = {
    low_energy: "Low energy",
    cravings: "Cravings",
    stress: "Stress drain",
    cognitive: "Cognitive fog",
  };

  var Q8_STRUGGLE = {
    low_energy: "Low energy",
    cravings: "Cravings",
    stress: "Stress",
    cognitive: "Brain fog",
  };

  /**
   * Derived quiz outputs for results + offer copy.
   * Risk is 0–1 (higher = worse NAD / cellular outlook).
   */
  function buildResultsProfile(answers, steps) {
    answers = answers || {};
    var q16 = findStepById(steps, "q16");
    var ageVal = answers.q16;
    var ageLabel = answerLabelForValue(q16, ageVal);
    var ageChip = ageLabel ? "Age range: " + ageLabel : "Age range: —";
    var actualRangeDisplay = ageLabel
      ? ageLabel.replace(/\u2013/g, "-").replace(/\u2014/g, "-")
      : "—";

    var energyNum = 5;
    if (answers.q17 != null && String(answers.q17).trim() !== "") {
      var en = parseInt(answers.q17, 10);
      if (!isNaN(en)) energyNum = Math.max(1, Math.min(10, en));
    }
    var energyChip = "Energy rating: " + energyNum + "/10";

    var symParts = [];
    var q7 = answers.q7;
    var q8 = answers.q8;
    if (Array.isArray(q7)) {
      for (var i = 0; i < q7.length; i++) {
        if (q7[i] === "none") continue;
        symParts.push(Q7_CHIP[q7[i]] || q7[i]);
      }
    }
    if (Array.isArray(q8)) {
      for (var j = 0; j < q8.length; j++) {
        if (q8[j] === "none") continue;
        symParts.push(Q8_CHIP[q8[j]] || q8[j]);
      }
    }
    var symptomsChip =
      symParts.length > 0
        ? "Symptoms: " + symParts.slice(0, 3).join(", ")
        : "Symptoms: —";

    var struggleChip = "Struggle: —";
    if (Array.isArray(q8)) {
      var nf = q8.filter(function (x) {
        return x !== "none";
      });
      if (nf.length && Q8_STRUGGLE[nf[0]]) struggleChip = "Struggle: " + Q8_STRUGGLE[nf[0]];
    }

    var symptomsReportedChip =
      symParts.length > 0
        ? "Symptoms reported: " + symParts.slice(0, 3).join(", ")
        : "Symptoms reported: —";

    /* --- Risk model (weighted answers → normalized 0–1, ProDenta-style) --- */
    var CK = window.CataKorQuizScoring;
    var risk;
    if (CK && typeof CK.computeRisk === "function") {
      risk = CK.computeRisk(answers).normalized;
    } else {
      risk = 0;
      risk += ((10 - energyNum) / 9) * 0.36;
      var cnt7 = Array.isArray(q7) ? q7.filter(function (x) {
        return x !== "none";
      }).length : 0;
      var cnt8 = Array.isArray(q8) ? q8.filter(function (x) {
        return x !== "none";
      }).length : 0;
      risk += Math.min((cnt7 + cnt8) / 8, 1) * 0.24;
      if (answers.q4 === "yes") risk += 0.07;
      var sleep = answers.q2;
      if (sleep === "exhausted") risk += 0.1;
      else if (sleep === "tired") risk += 0.06;
      else if (sleep === "varies") risk += 0.03;
      var caf = answers.q3;
      if (caf === "essential") risk += 0.07;
      else if (caf === "high") risk += 0.04;
      if (ageVal === "65_plus") risk += 0.11;
      else if (ageVal === "55_64") risk += 0.07;
      else if (ageVal === "45_54") risk += 0.04;
      if (answers.q9 === "yes") risk += 0.05;
      if (risk > 1) risk = 1;
    }

    /* Results must always land on the depleted side for the NAD/cellular gauges. */
    var depletedRisk = Math.max(risk, 0.36);

    var nadPct = Math.round(16 + depletedRisk * 64);
    nadPct = Math.max(39, Math.min(88, nadPct));

    var nadTier = depletedRisk < 0.67 ? "declining" : "critical";
    var nadMarkerPct =
      nadTier === "optimal" ? "16%" : nadTier === "declining" ? "49.5%" : "83%";
    var nadVerdict =
      nadTier === "optimal"
        ? "Within optimal range"
        : nadTier === "declining"
        ? "Moderate decline"
        : "Significant decline";

    var cellularScore = Math.round(
      Math.max(12, Math.min(98, 100 - risk * 62 - (10 - energyNum) * 1.15))
    );
    cellularScore = Math.min(66, cellularScore);

    var cellTier =
      cellularScore >= 42 ? "declining" : "critical";
    var cellMarkerPct =
      cellTier === "optimal" ? "16%" : cellTier === "declining" ? "49.5%" : "83%";
    var cellVerdict =
      cellTier === "critical"
        ? "Critically low"
        : cellTier === "declining"
        ? "Below optimal"
        : "Moderate";

    var result2Headline =
      cellularScore < 34
        ? "critically low"
        : cellularScore < 50
        ? "very low"
        : cellularScore < 66
        ? "below average"
        : "suboptimal";

    /* Biological age: cohort midpoint + stress — purely illustrative */
    var mid =
      ageVal === "65_plus"
        ? 68
        : ageVal === "55_64"
        ? 60
        : ageVal === "45_54"
        ? 50
        : ageVal === "35_44"
        ? 40
        : ageVal === "25_34"
        ? 30
        : ageVal === "18_24"
        ? 21
        : 45;
    var bandMax =
      ageVal === "65_plus"
        ? 80
        : ageVal === "55_64"
        ? 64
        : ageVal === "45_54"
        ? 54
        : ageVal === "35_44"
        ? 44
        : ageVal === "25_34"
        ? 34
        : ageVal === "18_24"
        ? 24
        : 70;
    var bioYears = Math.round(mid + risk * 20 + (10 - energyNum) * 1.4);
    bioYears = Math.max(22, Math.min(84, bioYears));
    var bioYearsDisplay = String(bioYears);
    var bioAgeRightLabel =
      bioYears >= Math.max(bandMax + 8, 62)
        ? "Your biological age: 65+"
        : "Your biological age: " + bioYearsDisplay + " years";

    /* Result 4 — four-tier horizontal bar (critical … optimal), marker snaps to band center */
    var r4TierIdx = cellTier === "critical" ? 0 : 1;
    var r4Markers = ["14%", "38%", "62%", "86%"];
    var r4Verdicts = [
      "Critically low production",
      "Below optimal",
      "Moderate production",
      "Near optimal",
    ];

    var offerProtocolStats =
      "NAD+ Decline: " +
      nadPct +
      "% · Cellular Energy: " +
      cellularScore +
      "/100 · Biological Age: " +
      bioYearsDisplay +
      " years";

    return {
      __CK_CHIP_SYMPTOMS__: escapeHtml(symptomsChip),
      __CK_CHIP_AGE__: escapeHtml(ageChip),
      __CK_CHIP_STRUGGLE__: escapeHtml(struggleChip),
      __CK_CHIP_ENERGY__: escapeHtml(energyChip),
      __CK_SYMPTOMS_REPORTED__: escapeHtml(symptomsReportedChip),
      __CK_NAD_PCT__: escapeHtml(String(nadPct)),
      __CK_NAD_VERDICT__: escapeHtml(nadVerdict),
      __CK_NAD_MARKER__: escapeHtml(nadMarkerPct),
      __CK_BIO_YEARS__: escapeHtml(bioYearsDisplay),
      __CK_BIO_ACTUAL_RANGE__: escapeHtml(actualRangeDisplay),
      __CK_BIO_VS_LABEL__: escapeHtml(bioAgeRightLabel),
      __CK_CELL_SCORE__: escapeHtml(String(cellularScore)),
      __CK_CELL_VERDICT__: escapeHtml(cellVerdict),
      __CK_CELL_MARKER__: escapeHtml(cellMarkerPct),
      __CK_RESULT2_HEADLINE__: escapeHtml(result2Headline),
      __CK_R4_VERDICT__: escapeHtml(r4Verdicts[r4TierIdx]),
      __CK_R4_MARKER__: escapeHtml(r4Markers[r4TierIdx]),
      __CK_OFFER_PROTOCOL_STATS__: escapeHtml(offerProtocolStats),
      __CK_OFFER_BIO_GAP__: escapeHtml(
        String(Math.max(0, bioYears - Math.round(mid)))
      ),
    };
  }

  function applyResultsTokens(html, profile) {
    if (!html || !profile) return html || "";
    var out = html;
    for (var key in profile) {
      if (!Object.prototype.hasOwnProperty.call(profile, key)) continue;
      var re = new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
      out = out.replace(re, profile[key]);
    }
    return out;
  }

  function quizApp() {
    return {
      steps: [],
      step: 0,
      answers: {},
      _autoAdvanceTimer: null,
      _offerTimerInterval: null,
      offerPurchaseType: "subscription",
      selectedOfferBundleId: "six-month",
      offerTimer: {
        hours: "23",
        minutes: "50",
        seconds: "00",
      },

      get total() {
        return this.steps.length;
      },

      get currentStep() {
        return this.steps[this.step] || null;
      },

      /** Static HTML with __CK_*__ tokens filled for results + offer */
      get renderedStaticHtml() {
        var stepIx = this.step;
        var s = this.steps[stepIx];
        if (!s || !s.html) return "";
        if (s.layout === "results" || s.layout === "offer") {
          var profile = buildResultsProfile(this.answers, this.steps);
          return resolveAssetPath(applyResultsTokens(s.html, profile));
        }
        return s.html;
      },

      get currentQuestion() {
        var s = this.currentStep;
        return s && s.stepType === "question" ? s : null;
      },

      isFemaleIntro: function () {
        return this.answers && this.answers.intro === "female";
      },

      questionText: function (step) {
        if (!step) return "";
        if (step.id === "q15") {
          return this.isFemaleIntro()
            ? "How would you describe where you are with hormones?"
            : "How would you describe your testosterone / vitality?";
        }
        return step.question || "";
      },

      questionAnswers: function (step) {
        if (!step) return [];
        if (step.id === "q15" && !this.isFemaleIntro()) {
          return [
            { id: "a1", text: "Declining noticeably", value: "declining_noticeably" },
            { id: "a2", text: "Slowly slipping", value: "slowly_slipping" },
            { id: "a3", text: "Still solid", value: "still_solid" },
            { id: "a4", text: "Not sure", value: "not_sure" },
          ];
        }
        return step.answers || [];
      },

      get progressSteps() {
        return this.steps.filter(function (s) {
          return s.hideTopbar !== true && s.layout !== "results";
        });
      },

      get currentProgressIndex() {
        var s = this.currentStep;
        if (!s || s.hideTopbar === true || s.layout === "results") return 0;
        var ps = this.progressSteps;
        for (var i = 0; i < ps.length; i++) {
          if (ps[i].id === s.id) return i + 1;
        }
        return 0;
      },

      get topbarCounter() {
        var idx = this.currentProgressIndex;
        var total = this.progressSteps.length;
        if (!idx || !total) return "";
        return idx + " of " + total;
      },

      get resultsSteps() {
        return this.steps.filter(function (s) { return s.layout === "results"; });
      },

      get isOnResults() {
        return !!(this.currentStep && this.currentStep.layout === "results");
      },

      get currentResultIndex() {
        var s = this.currentStep;
        if (!s || s.layout !== "results") return 0;
        var rs = this.resultsSteps;
        for (var i = 0; i < rs.length; i++) {
          if (rs[i].id === s.id) return i + 1;
        }
        return 0;
      },

      get resultsSegments() {
        var total = this.resultsSteps.length;
        var idx = this.currentResultIndex;
        var segs = [];
        for (var i = 1; i <= total; i++) {
          segs.push({ filled: i <= idx, index: i });
        }
        return segs;
      },

      get isCurrentStepRequired() {
        var s = this.currentStep;
        return s && s.required !== false;
      },

      get isCurrentStepComplete() {
        return this.isStepComplete(this.currentStep);
      },

      get showFooterNextButton() {
        var s = this.currentStep;
        if (!s) return false;
        return !(s.stepType === "question" && s.type === "single");
      },

      init: function () {
        var self = this;
        this.loadState();

        var url = window.CATA_KOR_QUESTIONS_URL || window.CATAKOR_QUESTIONS_URL || "cata-kor-quiz-questions.json";
        fetch(url)
          .then(function (r) { return r.json(); })
          .then(function (json) {
            json = resolveAssetsInValue(json);
            self.steps = normalizeToSteps(json);
            self.normalizeStep();
            self.persist();
            setTimeout(function () {
              self.handleStepEnter();
              self.updateUrlWithStep();
            }, 50);
          })
          .catch(function (err) {
            console.error("[cata-kor-quiz] failed to load questions:", err);
          });

        document.addEventListener("click", function (e) {
          var btn = e.target.closest("[data-quiz-action]");
          if (!btn) return;
          var setKey = btn.getAttribute("data-quiz-set-key");
          var setValue = btn.getAttribute("data-quiz-set-value");
          if (setKey && setValue !== null) {
            self.setStepAnswer(setKey, setValue);
          }
          var action = btn.getAttribute("data-quiz-action");
          if (action === "next") self.next();
          else if (action === "back") self.back();
        });
      },

      loadState: function () {
        try {
          var savedStep = localStorage.getItem(STORAGE_STEP);
          if (savedStep !== null) {
            var n = parseInt(savedStep, 10);
            if (!isNaN(n) && n >= 0) this.step = n;
            else this._savedStepId = savedStep;
          }
          var savedAnswers = localStorage.getItem(STORAGE_ANSWERS);
          if (savedAnswers) {
            this.answers = JSON.parse(savedAnswers);
            if (typeof this.answers !== "object" || this.answers === null) {
              this.answers = {};
            }
          }
        } catch (_) {
          this.answers = {};
        }

        // Allow ?step=ID in URL to jump to a known step on load
        var urlParams = getUrlParams();
        if (urlParams.step) this._urlStepId = urlParams.step;
      },

      normalizeStep: function () {
        // Resolve string-id step references (saved or from URL) to numeric index
        var targetId = this._urlStepId || this._savedStepId;
        if (targetId && this.steps.length > 0) {
          for (var i = 0; i < this.steps.length; i++) {
            if (this.steps[i].id === targetId) {
              this.step = i;
              break;
            }
          }
          this._urlStepId = null;
          this._savedStepId = null;
        }
        if (this.step >= this.total) this.step = Math.max(0, this.total - 1);
        if (this.step < 0) this.step = 0;
      },

      isStepComplete: function (s) {
        if (!s || !s.id) return false;
        var v = this.answers[s.id];
        if (s.stepType === "question") {
          if (s.type === "multiple") return Array.isArray(v) && v.length > 0;
          if (s.type === "single") return v != null && String(v).trim() !== "";
          return v !== undefined && v !== null && v !== "";
        }
        if (s.stepType === "slider") {
          return v != null && String(v).trim() !== "";
        }
        if (s.stepType === "static") return true;
        if (s.stepType === "email_capture") {
          return v && v.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v.email).trim());
        }
        if (s.stepType === "product_recommendation") {
          return true;
        }
        return true;
      },

      sliderValue: function (step) {
        var v = this.getStepAnswer(step.id);
        if (v == null || v === "") return step.default != null ? step.default : (step.min || 1);
        var n = parseInt(v, 10);
        return isNaN(n) ? (step.default || step.min || 1) : n;
      },

      sliderFillPct: function (step) {
        var v = this.sliderValue(step);
        var min = step.min != null ? step.min : 1;
        var max = step.max != null ? step.max : 10;
        if (max === min) return 0;
        return ((v - min) / (max - min)) * 100;
      },

      sliderLabel: function (step) {
        var v = this.sliderValue(step);
        if (step.labels && step.labels[String(v)]) return step.labels[String(v)];
        return "";
      },

      persist: function () {
        try {
          var stepId = this.currentStep ? this.currentStep.id : "";
          localStorage.setItem(STORAGE_STEP, stepId || String(this.step));
          localStorage.setItem(STORAGE_ANSWERS, JSON.stringify(this.answers));
        } catch (_) {}
      },

      updateUrlWithStep: function () {
        if (!this.steps || this.steps.length === 0) return;
        var s = this.currentStep;
        if (!s || !s.id) return;
        try {
          var base = window.location.protocol + "//" + window.location.host + window.location.pathname;
          var params = ["step=" + encodeURIComponent(s.id)];
          var newUrl = base + "?" + params.join("&");
          window.history.replaceState({}, "", newUrl);
        } catch (e) {
          // silent fail
        }
      },

      handleStepEnter: function () {
        var self = this;
        this.clearAutoAdvance();
        this.clearOfferTimer();

        var s = this.currentStep;
        if (!s) return;

        injectStepCss(s.css || "");

        if (s.stepType === "offer") {
          this.startOfferTimer();
        }

        if (s.id === "preparing-summary") {
          this._startLoadingProgress(s.autoAdvance || 5500);
        }

        if (s.autoAdvance && typeof s.autoAdvance === "number" && s.autoAdvance > 0) {
          this._autoAdvanceTimer = setTimeout(function () {
            self.next();
          }, s.autoAdvance);
        }
      },

      _startLoadingProgress: function (duration) {
        var attempts = 0;
        function start() {
          var pctEl = document.querySelector(".loading-progress-pct");
          if (!pctEl) {
            if (attempts++ < 30) {
              requestAnimationFrame(start);
            }
            return;
          }
          pctEl.textContent = "0%";
          var startTime = null;
          function tick(timestamp) {
            if (startTime === null) startTime = timestamp;
            var elapsed = timestamp - startTime;
            var pct = Math.min(100, Math.round((elapsed / duration) * 100));
            pctEl.textContent = pct + "%";
            if (elapsed < duration) {
              requestAnimationFrame(tick);
            }
          }
          requestAnimationFrame(tick);
        }
        requestAnimationFrame(start);
      },

      clearAutoAdvance: function () {
        if (this._autoAdvanceTimer) {
          clearTimeout(this._autoAdvanceTimer);
          this._autoAdvanceTimer = null;
        }
      },

      startOfferTimer: function () {
        var self = this;
        var cfg = this.offerConfig();
        var remaining = cfg && cfg.promo && cfg.promo.expiresInSeconds
          ? parseInt(cfg.promo.expiresInSeconds, 10)
          : 85800;
        if (isNaN(remaining) || remaining < 0) remaining = 85800;

        function render() {
          var hours = Math.floor(remaining / 3600);
          var minutes = Math.floor((remaining % 3600) / 60);
          var seconds = remaining % 60;
          self.offerTimer = {
            hours: self.padTimer(hours),
            minutes: self.padTimer(minutes),
            seconds: self.padTimer(seconds),
          };
          remaining = Math.max(0, remaining - 1);
        }

        render();
        this._offerTimerInterval = setInterval(render, 1000);
      },

      clearOfferTimer: function () {
        if (this._offerTimerInterval) {
          clearInterval(this._offerTimerInterval);
          this._offerTimerInterval = null;
        }
      },

      padTimer: function (value) {
        value = parseInt(value, 10);
        if (isNaN(value)) value = 0;
        return value < 10 ? "0" + value : String(value);
      },

      next: function () {
        var self = this;
        this.clearAutoAdvance();
        if (this.step < this.total - 1) {
          this.step += 1;
          this.persist();
          this.updateUrlWithStep();
          window.scrollTo({ top: 0, behavior: "instant" });
          setTimeout(function () { self.handleStepEnter(); }, 50);
        }
      },

      back: function () {
        var self = this;
        this.clearAutoAdvance();
        if (this.step > 0) {
          this.step -= 1;
          this.persist();
          this.updateUrlWithStep();
          window.scrollTo({ top: 0, behavior: "instant" });
          setTimeout(function () { self.handleStepEnter(); }, 50);
        }
      },

      // Question selection
      isSelected: function (option) {
        var q = this.currentQuestion;
        if (!q) return false;
        var v = this.answers[q.id];
        var val = option.value !== undefined ? option.value : option.id;
        if (q.type === "multiple") {
          return Array.isArray(v) && v.indexOf(val) >= 0;
        }
        return v === val;
      },

      setAnswer: function (option) {
        var q = this.currentQuestion;
        if (!q) return;
        var val = option.value !== undefined ? option.value : option.id;
        this.answers[q.id] = val;
        this.persist();
        if (q.type === "single") {
          var self = this;
          setTimeout(function () {
            self.next();
          }, 80);
        }
      },

      toggleAnswer: function (option) {
        var q = this.currentQuestion;
        if (!q || q.type !== "multiple") return;
        var val = option.value !== undefined ? option.value : option.id;
        var arr = Array.isArray(this.answers[q.id]) ? this.answers[q.id].slice() : [];
        var i = arr.indexOf(val);
        function normalizeText(s) {
          return String(s || "")
            .toLowerCase()
            .replace(/\s+/g, " ")
            .trim();
        }
        function isNoneChoice(opt) {
          if (!opt) return false;
          var optVal = normalizeText(opt.value !== undefined ? opt.value : opt.id);
          var optText = normalizeText(opt.text);
          return (
            optVal === "none" ||
            optVal === "nothing" ||
            optText === "none" ||
            optText === "none of the above" ||
            optText.indexOf("nothing yet") === 0
          );
        }
        function isNoneValue(selectedVal) {
          var answers = q.answers || [];
          for (var aIdx = 0; aIdx < answers.length; aIdx++) {
            var answer = answers[aIdx];
            var answerVal = answer.value !== undefined ? answer.value : answer.id;
            if (answerVal === selectedVal) return isNoneChoice(answer);
          }
          var normalized = normalizeText(selectedVal);
          return normalized === "none" || normalized === "nothing";
        }

        if (i >= 0) {
          arr.splice(i, 1);
        } else if (isNoneChoice(option)) {
          arr = [val];
        } else {
          arr = arr.filter(function (selectedVal) {
            return !isNoneValue(selectedVal);
          });
          arr.push(val);
        }
        this.answers[q.id] = arr;
        this.persist();
      },

      // Generic per-step answer setter (used by static-step buttons)
      setStepAnswer: function (stepId, value) {
        this.answers[stepId] = value;
        this.persist();
      },

      getStepAnswer: function (stepId) {
        return this.answers[stepId];
      },

      // Email capture (Phase 3)
      submitShopifyNewsletter: function (email) {
        var payload = new URLSearchParams();
        payload.append("form_type", "customer");
        payload.append("utf8", "\u2713");
        payload.append("contact[tags]", "newsletter");
        payload.append("contact[email]", email);

        return fetch("/contact", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          },
          body: payload.toString(),
          credentials: "same-origin",
        });
      },

      handleEmailInput: function (event, stepId) {
        this.setStepAnswer(stepId, { email: event.target.value });
      },

      isEmailValid: function (stepId) {
        var v = this.answers[stepId];
        if (!v || !v.email) return false;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v.email).trim());
      },

      submitEmailCapture: function (stepId) {
        if (!this.isEmailValid(stepId)) return;
        var email = this.answers[stepId].email.trim();
        try { localStorage.setItem(STORAGE_EMAIL, email); } catch (_) {}
        var self = this;
        this.submitShopifyNewsletter(email)
          .then(function (response) {
            if (!response.ok) {
              console.warn("[cata-kor-quiz] newsletter submit failed:", response.status);
            }
          })
          .catch(function (error) {
            console.warn("[cata-kor-quiz] newsletter submit error:", error);
          })
          .finally(function () {
            self.next();
          });
      },

      // Product recommendation (Phase 4)
      getPurchaseType: function (stepId) {
        var a = this.answers[stepId];
        return (a && a.purchaseType) || "subscription";
      },

      setPurchaseType: function (stepId, type) {
        var a = this.answers[stepId] || {};
        a.purchaseType = type;
        this.answers[stepId] = a;
        this.persist();
      },

      // Dedicated offer step
      offerConfig: function () {
        return this.currentStep && this.currentStep.stepType === "offer" ? this.currentStep : {};
      },

      offerProfile: function () {
        var profile = buildResultsProfile(this.answers, this.steps);
        return {
          bioGap: profile.__CK_OFFER_BIO_GAP__ || "0",
          protocolStats: profile.__CK_OFFER_PROTOCOL_STATS__ || "NAD+ production: moderate; cellular score: 72/100",
        };
      },

      offerBundles: function () {
        var bundles = (this.offerConfig().bundles || []).slice();
        return bundles.sort(function (a, b) {
          return (a.mobileOrder || 0) - (b.mobileOrder || 0);
        });
      },

      offerBundlePricing: function (bundle) {
        if (!bundle || !bundle.pricing) return {};
        return bundle.pricing[this.offerPurchaseType] || bundle.pricing.subscription || {};
      },

      offerBundleFeatures: function (bundle) {
        if (!bundle || !bundle.features) return [];
        if (Array.isArray(bundle.features)) return bundle.features;
        if (typeof bundle.features === "object") {
          return bundle.features[this.offerPurchaseType] || bundle.features.subscription || bundle.features.oneTime || [];
        }
        return [];
      },

      offerBundleHasCompareAt: function (bundle) {
        var pricing = this.offerBundlePricing(bundle);
        return !!(pricing && pricing.compareAt && String(pricing.compareAt).trim());
      },

      offerBundleOrderTotal: function (bundle) {
        var pricing = this.offerBundlePricing(bundle);
        if (!pricing) return "";
        return pricing.total || pricing.pricePerBottle || "";
      },

      offerBundleShowPerBottle: function (bundle) {
        return !(this.offerPurchaseType === "subscription" && bundle && bundle.id === "one-month");
      },

      offerBundleShowPerBottleLabel: function (bundle) {
        return !(this.offerPurchaseType === "oneTime" && bundle && bundle.id === "one-month");
      },

      setOfferPurchaseType: function (type) {
        this.offerPurchaseType = type === "oneTime" ? "oneTime" : "subscription";
      },

      selectOfferBundle: function (bundleId) {
        this.selectedOfferBundleId = bundleId;
        var bundles = this.offerConfig().bundles || [];
        var selected = null;
        for (var i = 0; i < bundles.length; i++) {
          if (bundles[i].id === bundleId) {
            selected = bundles[i];
            break;
          }
        }
        if (!selected || !selected.variantIds) return;
        var variantId = selected.variantIds[this.offerPurchaseType];
        var sellingPlanId = null;
        if (this.offerPurchaseType === "subscription" && selected.sellingPlanIds) {
          sellingPlanId = selected.sellingPlanIds.subscription || selected.sellingPlanIds[this.offerPurchaseType] || null;
        }
        this.addOfferVariantToCart(variantId, sellingPlanId);
      },

      addOfferVariantToCart: function (variantId, sellingPlanId) {
        if (!isRealShopifyVariantId(variantId)) {
          console.warn("[cata-kor-quiz] Missing real Shopify variant ID:", variantId);
          return;
        }
        var payload = { id: Number(variantId), quantity: 1 };
        if (isRealShopifySellingPlanId(sellingPlanId)) {
          payload.selling_plan = Number(sellingPlanId);
        }
        fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
          },
          body: JSON.stringify(payload),
        })
          .then(function (response) {
            if (!response.ok) throw new Error("Unable to add variant to cart");
            return response.json();
          })
          .then(function () {
            window.location.href = window.CATA_KOR_CHECKOUT_REDIRECT || "/checkout";
          })
          .catch(function (error) {
            console.error("[cata-kor-quiz] cart add failed:", error);
            window.location.href = "/cart";
          });
      },

      offerProtocolItems: function () {
        var profile = this.offerProfile();
        return resolveAssetsInValue([
          {
            icon: "assets/images/offer/icon-protocol-plan.svg",
            title: "Recommended plan:",
            text: " 6-month NAD+ restoration (matches your decline level)",
          },
          {
            icon: "assets/images/offer/icon-protocol-daily.svg",
            title: "Daily protocol:",
            text: " 2 capsules with breakfast — LipoNAD™ for max absorption",
          },
          {
            icon: "assets/images/offer/icon-protocol-timeline.svg",
            title: "Expected timeline:",
            text: " Energy lift in 2–4 weeks · Full cellular restoration by month 3–4",
          },
          {
            icon: "assets/images/offer/icon-protocol-stats.svg",
            title: "Your starting point:",
            text: " " + profile.protocolStats,
          },
          {
            icon: "assets/images/offer/icon-revert.svg",
            title: "Track your progress:",
            text: " Re-take this quiz at day 60 to see your scores change",
          },
        ]);
      },

      offerComparisonRows: function () {
        return [
          { label: "Patented liposomal delivery (LipoNAD™)", brandIcon: true, otherIcon: "no" },
          { label: "9.3x higher absorption", brandIcon: true, otherIcon: "no" },
          { label: "NSF Certified", brandIcon: true, otherIcon: "no" },
          { label: "ISO/IEC 17025 Lab Tested", brandIcon: true, otherText: "Sometimes" },
          { label: "Trans-Resveratrol synergy", brandIcon: true, otherIcon: "no" },
          { label: "Made in USA", brandIcon: true, otherText: "Varies" },
          { label: "90-day money-back guarantee", brandIcon: true, otherText: "Usually 30 days" },
          { label: "Cost per day (6-month plan)", brandText: "$0.63", otherText: "$1.50–$3.00" },
        ];
      },

      offerFaqs: function () {
        return [
          { question: "How should I take the NAD supplement?", answer: "Take 2 capsules every day with or without food.", open: true },
          { question: "How long does it take to see results?", answer: "Most users report noticeable changes within a few weeks of consistent daily use, particularly in energy and overall well-being." },
          { question: "Are there side effects?", answer: "As the body adjusts to higher NAD+ levels, temporary effects like mild headaches may occur. These are typically short-lived. Starting with a lower dose may help." },
          { question: "Is this NAD+ made in the USA?", answer: "Yes. Our supplement is Made in USA from Globally Sourced Ingredients, also manufactured and quality-tested in the United States in a GMP-certified facility to ensure safety, purity, and consistency." },
        ];
      },
    };
  }

  // Expose globally so Alpine's `x-data="quizApp()"` resolves regardless of script load order.
  window.quizApp = quizApp;

  document.addEventListener("alpine:init", function () {
    if (window.Alpine) window.Alpine.data("quizApp", quizApp);
  });

  // Touch quiz id on load so sessions track from first paint
  getQuizId();
})();
